import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface CannedResponse {
  id: string;
  title: string;
  body: string;
  category: string | null;
  shortcut: string | null;
  isShared: boolean;
  createdBy: string;
  usageCount: number;
  createdAt: string;
}

export function useCannedResponses(search?: string, category?: string) {
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    try {
      const res = await apiFetch<{ data: CannedResponse[] }>(`/crm/canned-responses?${params}`);
      setResponses(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load canned responses");
    } finally {
      setIsLoading(false);
    }
  }, [search, category]);

  useEffect(() => { fetch(); }, [fetch]);
  return { responses, isLoading, error, refetch: fetch };
}

export async function createCannedResponse(data: {
  title: string; body: string; category?: string; shortcut?: string; isShared?: boolean;
}): Promise<CannedResponse> {
  const res = await apiFetch<{ data: CannedResponse }>("/crm/canned-responses", {
    method: "POST", body: JSON.stringify(data),
  });
  return res.data;
}

export async function updateCannedResponse(id: string, data: Partial<{
  title: string; body: string; category: string | null; shortcut: string | null; isShared: boolean;
}>): Promise<CannedResponse> {
  const res = await apiFetch<{ data: CannedResponse }>(`/crm/canned-responses/${id}`, {
    method: "PATCH", body: JSON.stringify(data),
  });
  return res.data;
}

export async function deleteCannedResponse(id: string): Promise<void> {
  await apiFetch(`/crm/canned-responses/${id}`, { method: "DELETE" });
}

export async function recordUsage(id: string): Promise<void> {
  await apiFetch(`/crm/canned-responses/${id}/use`, { method: "POST" });
}
